/**
 * Centralized field labels enum
 * Maintains consistent labeling across the entire application
 * (Tables, Forms, Filters, Reports, etc.)
 */

export enum FieldLabels {
  // Patient Information
  PATIENT_ID = 'YH No.',
  PATIENT_NAME = 'Patient Name',
  PATIENT_AGE = 'Patient Age',
  PATIENT_SEX = 'Patient Sex',
  PATIENT_GENDER = 'Patient Gender',
  PATIENT_DOB = 'Patient Birth Date',
  MRN = 'MRN',

  // Patient Information (Short Forms - for tables)
  PATIENT_ID_SHORT = 'YH No.',
  PATIENT_AGE_SHORT = 'Pat. Age',
  PATIENT_SEX_SHORT = 'Pat. Sex',

  PATIENT_REPORT_AGE = 'Age',
  PATIENT_REPORT_SEX = 'Sex',

  // Study Information
  MODALITY = 'Modality',
  MODALITY_SHORT = 'Mod.',
  BODY_PART = 'Body Part',
  STUDY_DESCRIPTION = 'Study Description',
  STUDY_DESCRIPTION_SHORT = 'Study Desc.',
  ITEM_DESCRIPTION = 'Item Desc.',
  STUDY_DATE = 'Study Date',
  SCAN_DATE = 'Modality Scan Date',
  RECEIVED_DATE = 'Received Date',
  SITE = 'Site',
  HISTORY = 'History',
  ITEM_CODE = 'Item Code',
  SERIES_COUNT = 'Ser/images ',

  // Order Information
  ORDER_NUMBER = 'Order No',
  VISIT_ID = 'Visit ID',
  ACCESSION_NUMBER = 'Accession Number',
  ACCESSION_NUMBER_SHORT = 'Acc. No',
  ACCESSION_NUMBER_SHORTEST = 'Acc No',
  REFERRING_DOCTOR = 'Ref Doc',
  REFERRING_PHYSICIAN = 'Referring Physician',
  REFERRING_PHYSICIAN_SHORT = 'Ref. Phys.',
  REGISTRATION_TYPE = 'Reg. Type',
  ORDER_DATETIME = 'Order Date',

  // Status & Assignment
  STATUS = 'Status',
  PRIORITY = 'Priority',
  ASSIGNED_TO = 'Assigned To',
  ASSIGNED_RADIOLOGIST = 'Assigned to',
  TECHNICIAN = 'Technician',
  REPORTED_BY = 'Reported by',
  SIGNED_BY = 'Signed by',

  // Technical Details
  AE_TITLE = 'AE Title',
  INSTANCES = 'Instances',
  SERIES = 'Series',
  DESCRIPTION = 'Description',

  // Actions
  ACTIONS = 'Actions',
}

/**
 * Permissions enum
 * Maintains consistent permission keys across the application
 */
export enum Permissions {
  CONFIRM_CASES = 'CONFIRM_CASES',
  CREATE_TECH_NOTES = 'CREATE_TECH_NOTES',
  ASSIGN_CASES = 'ASSIGN_CASES',
  MODIFY_STUDY_DETAILS = 'MODIFY_STUDY_DETAILS',
  UPLOAD_DICOM = 'UPLOAD_DICOM',
  // EXPORT_IMAGE is labelled "Ability to Download Study" in the UI; code kept for stability.
  EXPORT_IMAGE = 'EXPORT_IMAGE',
  REPORT_FROM_PACS = 'REPORT_FROM_PACS',
  VIEW_DISPATCH_REPORT = 'VIEW_DISPATCH_REPORT',
  QC_WORKSTATION = 'QC_WORKSTATION',
  MARK_NOT_FOR_REPORTING = 'MARK_NOT_FOR_REPORTING',
  // EDIT_SIGNED_REPORTS is labelled "Can re-report signed-off study" in the UI; code kept for stability.
  EDIT_SIGNED_REPORTS = 'EDIT_SIGNED_REPORTS',
  // MANAGE_LIB_FOLDERS is labelled "Can Push Cases to Academic Server" in the UI; code kept for stability.
  MANAGE_LIB_FOLDERS = 'MANAGE_LIB_FOLDERS',
  // Accept a known modality shortfall and mark an order reconciled anyway.
  // Narrower than MODIFY_STUDY_DETAILS (which gates the rest of the order↔scan
  // adoption flow) because a persistent shortfall usually means the item's
  // modality group is wrong for that centre, and forcing hides that.
  FORCE_RECONCILE = 'FORCE_RECONCILE',
}

/**
 * Display labels for the PACS workflow status (order_workflow.ow_pacs_status).
 *
 * Decouples what tables render from the stored DB enum: the value stays
 * NOT_FOR_REPORTING everywhere (filters, status toggles, backend), but the
 * status column shows a shorter label so it fits the constrained, sticky
 * status column instead of overflowing. Anything not listed here falls back to
 * the raw value with underscores swapped for spaces.
 */
export const PACS_STATUS_LABELS: Record<string, string> = {
  NOT_FOR_REPORTING: 'NOT READY',
  PENDING: 'NEW',
  CANCELLED: 'CANCELLED',
  SCANNED: 'UNREPORTED',
};

export const getPacsStatusLabel = (status?: string | null): string => {
  if (!status) return '';
  return PACS_STATUS_LABELS[status] ?? status.replaceAll('_', ' ');
};

/**
 * Orders page shows the raw 'SCANNED' status instead of the 'UNREPORTED'
 * relabel used in the PACS/Worklist/Dispatch scan lists — everything else
 * (NEW, NOT READY, etc.) still uses the shared label.
 */
export const getOrdersStatusLabel = (status?: string | null): string => {
  if (status === 'SCANNED') return 'SCANNED';
  return getPacsStatusLabel(status);
};
